/**
 * 이 사이트에만 필요한 레이아웃. 색·간격·타이포는 전부 디자인시스템 변수(--st-*)를 경유한다 —
 * 값을 직접 박으면 토큰과 어긋난다 (docs/rules.md 디자인 절). 투명도가 필요한 자리는
 * color-mix()로 토큰을 섞는다 — 새 색을 만들지 않기 위해서다.
 *
 * 결은 reference/design의 토스 스크린샷 문법이다: 흰 바닥 위에 회색 밴드를 번갈아 얹고,
 * 굵은 한글 제목 + 큰 라운드 카드로 끊는다. 장식은 브랜드 코발트 한 색으로만 낸다.
 *
 * 예외는 `/field-docs` 하나다 — 사용자가 고른 시안이 다크·amber 라 아래 fieldDocsStyles()가
 * 그 페이지 안(`.fd`)으로만 다른 테마를 스코프한다. 값은 여전히 전부 토큰에서 온다.
 */
import { accents, themeVariables, themes } from '@cottonray/design-tokens'

export function siteStylesheet(): string {
  return `
/* 한글은 단어 단위로 끊어야 "업무 대/행소" 같은 파괴가 안 난다 */
.st-text { word-break: keep-all; }

/* 버튼 모양의 링크 — 전역 a:hover 밑줄을 끈다 */
a.st-btn:hover { text-decoration: none; }

.site {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* ── 헤더 ─────────────────────────────────────────── */
.site-header {
  position: sticky;
  top: 0;
  z-index: 10;
  /* 반투명 + 블러 — 스크롤해도 콘텐츠가 비쳐 가벼워 보인다 */
  background: color-mix(in srgb, var(--st-color-background) 84%, transparent);
  -webkit-backdrop-filter: blur(14px);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid var(--st-color-border);
}
.site-header__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--st-space-4);
  min-height: 64px;
}
.site-nav {
  display: flex;
  align-items: center;
  gap: var(--st-space-5);
}
.site-nav a {
  color: var(--st-color-text-muted);
  font-size: var(--st-font-size-sm);
  font-weight: var(--st-font-weight-semibold);
  transition: color var(--st-duration-fast) ease;
}
.site-nav a:hover { color: var(--st-color-text); text-decoration: none; }

.wordmark {
  display: inline-flex;
  align-items: center;
  gap: var(--st-space-2);
  font-size: var(--st-font-size-lg);
  font-weight: var(--st-font-weight-bold);
  letter-spacing: -0.02em;
  color: var(--st-color-text);
}
.wordmark:hover { text-decoration: none; }
/* 워드마크의 코발트 점 — 마크와 같은 브랜드 색으로 문장을 닫는다 */
.wordmark__dot { color: var(--st-color-accent); }

/* ── 밴드 ─────────────────────────────────────────── */
/* 회색 바닥과 흰 면을 번갈아 놓아 구획을 나눈다 — 선 대신 면으로 끊는 방식.
   라이트는 배경과 surface가 같은 흰색이라(2026-08-07 흰 바탕 결정) surface 토큰만으로는
   밴드가 사라진다 — surface-raised를 배경 쪽으로 섞어 두 테마 모두에서 한 단만 가라앉힌다 */
.band { padding: var(--st-space-20) 0; }
.band--surface {
  background: color-mix(in srgb, var(--st-color-surface-raised) 60%, var(--st-color-background));
}
.band--tight { padding: var(--st-space-16) 0; }

/* ── 히어로 ───────────────────────────────────────── */
.hero {
  position: relative;
  overflow: hidden;
  padding: var(--st-space-20) 0;
}
/* 코발트 빛무리 — accent-surface 토큰이라 다크에서는 짙은 남색 글로우로 알아서 가라앉는다 */
.hero::before {
  content: '';
  position: absolute;
  top: -280px;
  right: -180px;
  width: 640px;
  height: 640px;
  border-radius: var(--st-radius-full);
  background: radial-gradient(closest-side, var(--st-color-accent-surface), transparent);
  pointer-events: none;
}
.hero__grid {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 400px);
  gap: var(--st-space-12);
  align-items: center;
}
.hero .st-text--display { max-width: 16em; }
.hero__lead {
  margin-top: var(--st-space-5);
  max-width: 34em;
  font-size: var(--st-font-size-lg);
  line-height: var(--st-line-height-lg);
}
.hero__cta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-3);
  margin-top: var(--st-space-8);
}
.hero__visual img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: var(--st-radius-xl);
  /* 일러스트 바탕이 밝은 회백색이라 다크에서는 테두리가 액자 역할을 한다 */
  border: 1px solid var(--st-color-border);
}

/* 검증 가능한 사실 세 개 — 숫자 크게, 설명 작게. 구분은 선으로만 */
.hero__facts {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--st-space-6);
  margin-top: var(--st-space-16);
}
.fact { min-width: 0; }
.fact + .fact {
  border-left: 1px solid var(--st-color-border);
  padding-left: var(--st-space-6);
}
.fact__value {
  font-size: var(--st-font-size-2xl);
  line-height: var(--st-line-height-2xl);
  font-weight: var(--st-font-weight-bold);
  letter-spacing: -0.01em;
}
.fact__label { margin-top: var(--st-space-1); }

/* 모바일에서는 한 단 낮은 스케일로 — 값은 스케일 키 안에서만 고른다 */
@media (max-width: 768px) {
  .hero { padding: var(--st-space-12) 0; }
  .band { padding: var(--st-space-12) 0; }
  .hero__grid { grid-template-columns: 1fr; gap: var(--st-space-8); }
  .hero__visual { max-width: 400px; }
  .hero .st-text--display {
    font-size: var(--st-font-size-3xl);
    line-height: var(--st-line-height-3xl);
  }
  .hero__lead {
    font-size: var(--st-font-size-md);
    line-height: var(--st-line-height-md);
  }
  .hero__facts { margin-top: var(--st-space-12); gap: var(--st-space-4); }
  .fact + .fact { padding-left: var(--st-space-4); }
  .fact__value {
    font-size: var(--st-font-size-xl);
    line-height: var(--st-line-height-xl);
  }
}

/* ── 섹션 ─────────────────────────────────────────── */
.section__head { margin-bottom: var(--st-space-8); }
.section__head .st-text--label { display: block; margin-bottom: var(--st-space-2); }
.section__head .st-text--body { margin-top: var(--st-space-2); max-width: 34em; }

/* ── 서비스 카드 ──────────────────────────────────── */
/* 340 하한이면 컨테이너(960)에서 정확히 2×2 — 3+1로 고아 카드가 생기지 않는다 */
.svc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: var(--st-space-4);
}
.svc-card {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-3);
  color: inherit;
  transition:
    border-color var(--st-duration-normal) var(--st-easing-standard),
    box-shadow var(--st-duration-normal) var(--st-easing-standard);
}
/*
  카드 전체를 링크로 감싸던 시절의 hover(들어올림)는 없앴다 — 앱이 두 스토어에 올라가면서
  갈 곳이 둘 이상이 됐고, 카드 자체는 더 이상 눌리지 않는다 (Home.tsx §ServiceCard).
  대신 어느 카드를 보고 있는지는 테두리로만 알린다.
*/
.svc-card:hover {
  border-color: var(--st-color-accent);
  box-shadow: 0 12px 32px color-mix(in srgb, var(--st-color-accent) 12%, transparent);
}
@media (prefers-reduced-motion: reduce) {
  .svc-card { transition: none; }
}
.svc-card__top {
  display: flex;
  align-items: center;
  gap: var(--st-space-3);
}
.svc-card__icon {
  width: 48px;
  height: 48px;
  flex: none;
  border-radius: var(--st-radius-md);
  border: 1px solid var(--st-color-border);
}
.svc-card__title { flex: 1; min-width: 0; }
.svc-card__desc { flex: 1; }
/* 갈 곳 줄 — 제품 사이트 하나 + 스토어 둘까지 들어간다. 좁은 화면에서는 접힌다 */
.svc-card__links {
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-2);
  margin-top: auto;
}
.svc-card__links .st-btn { text-decoration: none; }

/* ── 일하는 방식 ──────────────────────────────────── */
.principles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--st-space-4);
}
/* 토스의 인셋 박스 — 흰 밴드 위 회색 면, 테두리 없이 면으로만 */
.principle {
  background: var(--st-color-surface-raised);
  border-radius: var(--st-radius-lg);
  padding: var(--st-space-6);
}
.principle__no { display: block; margin-bottom: var(--st-space-3); }
.principle__body { margin-top: var(--st-space-2); }

/* ── 문의 ─────────────────────────────────────────── */
.contact-card {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: var(--st-space-8);
  background: var(--st-color-surface);
  border: 1px solid var(--st-color-border);
  border-radius: var(--st-radius-xl);
  padding: var(--st-space-8);
}
.contact { display: flex; flex-direction: column; gap: var(--st-space-3); }
.contact__mail {
  font-size: var(--st-font-size-xl);
  line-height: var(--st-line-height-xl);
  font-weight: var(--st-font-weight-bold);
  letter-spacing: -0.01em;
  word-break: break-all;
}
/* 권리 행사 창구 — 일반 문의보다 한 단 낮춰 "기본은 왼쪽"임을 크기로 말한다 */
.contact__mail--secondary {
  font-size: var(--st-font-size-lg);
  line-height: var(--st-line-height-lg);
  font-weight: var(--st-font-weight-semibold);
}

/* ── 제품 지원 ────────────────────────────────────── */
.support-hero { padding: var(--st-space-16) 0 var(--st-space-12); }
.support-hero__title { margin-top: var(--st-space-3); }
.support-hero__lead {
  max-width: 34em;
  margin-top: var(--st-space-4);
  font-size: var(--st-font-size-lg);
  line-height: var(--st-line-height-lg);
}
.support-layout { display: flex; flex-direction: column; gap: var(--st-space-16); }
.support-steps {
  display: grid;
  gap: var(--st-space-3);
  margin: var(--st-space-6) 0 0;
  padding-left: var(--st-space-6);
}
.support-steps li { padding-left: var(--st-space-2); }
.support-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--st-space-4);
  margin-top: var(--st-space-6);
}
.support-card__body { margin-top: var(--st-space-2); }
.support-contact { display: flex; flex-direction: column; gap: var(--st-space-3); }
.support-contact__body { margin-top: var(--st-space-2); }

@media (max-width: 640px) {
  .support-hero { padding: var(--st-space-12) 0 var(--st-space-10); }
  .support-hero__title {
    font-size: var(--st-font-size-3xl);
    line-height: var(--st-line-height-3xl);
  }
  .support-hero__lead {
    font-size: var(--st-font-size-md);
    line-height: var(--st-line-height-md);
  }
}

/* ── 푸터 ─────────────────────────────────────────── */
.site-footer {
  margin-top: auto;
  padding: var(--st-space-12) 0;
  border-top: 1px solid var(--st-color-border);
}
.site-footer__top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--st-space-4);
  margin-bottom: var(--st-space-8);
}
.site-footer__links { display: flex; flex-wrap: wrap; gap: var(--st-space-5); }
.site-footer__links a {
  color: var(--st-color-text-muted);
  font-size: var(--st-font-size-sm);
}
/* 사업자 정보 — 구분 기호 없이 간격으로만 끊는다. 각 항목이 라벨을 갖고 있어
   좁은 화면에서 줄이 바뀌어도 어느 값인지 읽힌다 */
.site-footer__business {
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-1) var(--st-space-4);
  margin: 0 0 var(--st-space-2);
  padding: 0;
  list-style: none;
}

/* ── 문서(방침) ───────────────────────────────────── */
/* 방침 문서는 사이트 크롬 없이 단독으로 뜬다 (docs/rules.md) — 읽기 폭만 좁힌다 */
.doc {
  max-width: 720px;
  margin: 0 auto;
  padding: var(--st-space-16) var(--st-space-5) var(--st-space-20);
}
.doc__meta { margin-top: var(--st-space-1); }
.doc__intro { margin-top: var(--st-space-5); }
.doc section { margin-top: var(--st-space-8); }
.doc section p { margin-top: var(--st-space-2); }
.doc section ul {
  margin: var(--st-space-2) 0 0;
  padding-left: var(--st-space-5);
}
.doc section li { margin-top: var(--st-space-1); }

/* 보유기간·위탁·국외이전 표. 열이 여섯 개까지 가므로 좁은 화면에서는 표만 가로로 스크롤시킨다 —
   본문이 밀리면 문서 전체가 읽기 어려워진다 */
.doc__table-scroll { overflow-x: auto; margin-top: var(--st-space-3); }
.doc__table {
  border-collapse: collapse;
  width: 100%;
  min-width: 520px;
  font-size: var(--st-font-size-sm);
  line-height: var(--st-line-height-sm);
}
.doc__table th,
.doc__table td {
  border: 1px solid var(--st-color-border);
  padding: var(--st-space-2) var(--st-space-3);
  text-align: left;
  vertical-align: top;
  word-break: keep-all;
}
.doc__table th {
  background: var(--st-color-surface-raised);
  font-weight: var(--st-font-weight-medium);
  white-space: nowrap;
}

/* 방침 목록 — 주소를 아는 운영자용 색인 */
.doc-list { display: flex; flex-direction: column; gap: var(--st-space-2); margin-top: var(--st-space-8); }
.doc-list a { display: block; }
.doc-list a:hover { text-decoration: none; }
.doc-list a:hover .st-text--heading { color: var(--st-color-accent); }

${resignationStyles()}
${fieldDocsStyles()}
${tidecipherStyles()}
${supportFormStyles()}
${accountStyles()}
`
}

/** `/games/tidecipher` — 게임의 어두운 심해 UI와 같은 다크·teal 토큰을 사이트 크롬까지 잇는다 */
function tidecipherStyles(): string {
  const teal = accents.teal.dark
  return `
.site--tc {
  ${themeVariables(themes.dark)}
  --st-color-accent: ${teal.accent};
  --st-color-accent-hover: ${teal.accentHover};
  --st-color-accent-surface: ${teal.accentSurface};
  --st-color-text-on-accent: ${teal.textOnAccent};
  background: var(--st-color-background);
  color: var(--st-color-text);
}
.tc { overflow: hidden; background: var(--st-color-background); color: var(--st-color-text); }
.tc-hero { padding: var(--st-space-16) 0 var(--st-space-20); }
.tc-hero__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 430px);
  align-items: center;
  gap: var(--st-space-16);
}
.tc-brand {
  display: flex;
  align-items: center;
  gap: var(--st-space-3);
  color: var(--st-color-accent);
  font-family: var(--st-font-mono);
  font-size: var(--st-font-size-xl);
  font-weight: var(--st-font-weight-bold);
  letter-spacing: .08em;
}
.tc-brand img { width: 52px; height: 52px; object-fit: contain; }
.tc-h1 {
  margin: var(--st-space-6) 0 0;
  max-width: 9em;
  color: var(--st-color-text);
  font-size: clamp(3rem, 7vw, 5.5rem);
  line-height: .98;
  letter-spacing: -.055em;
}
.tc-lead {
  display: block;
  margin-top: var(--st-space-6);
  max-width: 32em;
  font-size: var(--st-font-size-lg);
  line-height: var(--st-line-height-lg);
}
.tc-store-row { display: flex; flex-wrap: wrap; gap: var(--st-space-3); margin-top: var(--st-space-8); }
.tc-store {
  min-width: 152px;
  padding: var(--st-space-3) var(--st-space-4);
  border: 1px solid var(--st-color-border-strong);
  border-radius: var(--st-radius-md);
  background: var(--st-color-surface);
}
.tc-store b, .tc-store small { display: block; }
.tc-store b { color: var(--st-color-text); }
.tc-store small { margin-top: 2px; color: var(--st-color-text-subtle); }
.tc-platform-note { display: block; margin-top: var(--st-space-4); }

.tc-hero__visual { position: relative; min-height: 650px; display: grid; place-items: center; }
.tc-phone {
  position: relative;
  z-index: 1;
  width: min(100%, 350px);
  padding: 10px;
  border: 1px solid var(--st-color-border-strong);
  border-radius: 34px;
  background: var(--st-color-background);
  box-shadow: 0 28px 80px color-mix(in srgb, var(--st-color-background) 60%, transparent);
  transform: rotate(1.5deg);
}
.tc-phone img { display: block; width: 100%; height: auto; border-radius: 25px; }
.tc-app-icon {
  position: absolute;
  z-index: 2;
  right: 0;
  bottom: var(--st-space-8);
  width: 118px;
  height: 118px;
  border: 1px solid var(--st-color-border-strong);
  border-radius: var(--st-radius-xl);
  box-shadow: 0 18px 48px color-mix(in srgb, var(--st-color-background) 70%, transparent);
}

.tc-band { padding: var(--st-space-20) 0; border-top: 1px solid var(--st-color-border); }
.tc-section-head { display: flex; flex-direction: column; gap: var(--st-space-2); margin-bottom: var(--st-space-10); }
.tc-rules { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--st-space-5); }
.tc-rule {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-3);
  min-height: 260px;
  padding: var(--st-space-6);
  border: 1px solid var(--st-color-border);
  border-radius: var(--st-radius-xl);
  background: var(--st-color-surface);
}
.tc-rule__no {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: var(--st-radius-full);
  background: var(--st-color-accent-surface);
  color: var(--st-color-accent);
  font-family: var(--st-font-mono);
  font-weight: var(--st-font-weight-bold);
}

.tc-band--boss { background: var(--st-color-surface); }
.tc-boss-grid {
  display: grid;
  grid-template-columns: minmax(260px, 360px) minmax(0, 1fr);
  align-items: center;
  gap: var(--st-space-16);
}
.tc-boss-shot {
  padding: 9px;
  border: 1px solid var(--st-color-border-strong);
  border-radius: 30px;
  background: var(--st-color-background);
  transform: rotate(-1.5deg);
}
.tc-boss-shot img { display: block; width: 100%; height: auto; border-radius: 22px; }
.tc-boss-title { margin-top: var(--st-space-3); }
.tc-boss-copy {
  display: block;
  margin-top: var(--st-space-5);
  max-width: 32em;
  font-size: var(--st-font-size-lg);
  line-height: var(--st-line-height-lg);
}
.tc-stat-row { display: grid; gap: var(--st-space-3); margin-top: var(--st-space-8); }
.tc-stat {
  display: flex;
  align-items: center;
  gap: var(--st-space-3);
  padding: var(--st-space-3) var(--st-space-4);
  border: 1px solid var(--st-color-border);
  border-radius: var(--st-radius-md);
  background: var(--st-color-background);
}
.tc-stat img { flex: none; object-fit: contain; }

@media (max-width: 860px) {
  .tc-hero { padding: var(--st-space-12) 0; }
  .tc-hero__grid, .tc-boss-grid { grid-template-columns: 1fr; gap: var(--st-space-10); }
  .tc-hero__visual { min-height: 0; }
  .tc-rules { grid-template-columns: 1fr; }
  .tc-rule { min-height: 0; }
  .tc-band { padding: var(--st-space-12) 0; }
  .tc-boss-shot { width: min(100%, 340px); }
}
`
}

/**
 * 현장서류 랜딩 — 시안 "다크 프로 작업대"(2026-08-11 사용자 선택).
 *
 * 값은 하나도 직접 박지 않는다. 다크 표면은 `themes.dark`, 액센트는 이미 디자인시스템에 있는
 * `accents.amber`(시험보소가 쓰는 그 세트)에서 가져와 **`.fd` 안으로만 스코프**한다 —
 * ThemeProvider 로 갈아끼우면 `<html data-theme>` 이 바뀌어 헤더·푸터와 다른 페이지까지 끌려간다.
 * CSS 커스텀 프로퍼티는 상속되므로 래퍼 하나에 얹으면 그 안의 공용 컴포넌트가 전부 따라온다.
 */
/** `/resignation-timeline` — 사이트 기본 테마 그대로, 폼과 카드 격자만 얹는다 */
function resignationStyles(): string {
  return `
.rt-hero { padding: var(--st-space-16) 0 var(--st-space-10); }
.rt-hero__title { margin-top: var(--st-space-3); }
.rt-hero__lead { margin-top: var(--st-space-4); max-width: 640px; }

.rt-form { padding: var(--st-space-6); }
.rt-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--st-space-5);
}
.rt-field { display: flex; flex-direction: column; gap: var(--st-space-2); }
.rt-field input[type='date'],
.rt-field input[type='number'],
.rt-field select {
  font: inherit;
  padding: var(--st-space-3);
  border: 1px solid var(--st-color-border);
  border-radius: var(--st-radius-md);
  background: var(--st-color-background);
  color: var(--st-color-text);
}
.rt-field--check { flex-direction: row; align-items: center; gap: var(--st-space-3); grid-column: 1 / -1; }
.rt-field--check input { width: 18px; height: 18px; accent-color: var(--st-color-accent); }

.rt-seg { display: flex; border: 1px solid var(--st-color-border); border-radius: var(--st-radius-md); overflow: hidden; }
.rt-seg button {
  flex: 1;
  font: inherit;
  padding: var(--st-space-3);
  border: 0;
  background: var(--st-color-background);
  color: var(--st-color-text-muted);
  cursor: pointer;
}
.rt-seg button.is-on { background: var(--st-color-accent); color: var(--st-color-accent-contrast); }

.rt-empty { margin-top: var(--st-space-8); text-align: center; }

.rt-cards {
  margin-top: var(--st-space-8);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--st-space-5);
}
.rt-card { padding: var(--st-space-6); display: flex; flex-direction: column; gap: var(--st-space-3); }
.rt-card--no { opacity: 0.55; }
.rt-card__head { display: flex; align-items: center; justify-content: space-between; gap: var(--st-space-3); }
.rt-card__head .st-badge { white-space: nowrap; }
.rt-card__dday { color: var(--st-color-accent); }
.rt-card__badge {
  margin-left: var(--st-space-2);
  font-size: var(--st-font-size-caption);
  padding: 2px var(--st-space-2);
  border-radius: var(--st-radius-sm);
  background: color-mix(in srgb, var(--st-color-accent) 12%, transparent);
}
.rt-card__lines { margin: 0; padding-left: var(--st-space-5); display: flex; flex-direction: column; gap: var(--st-space-2); }
.rt-card__source { font-size: var(--st-font-size-caption); }

.rt-actions { margin-top: var(--st-space-8); display: flex; align-items: center; gap: var(--st-space-4); flex-wrap: wrap; }
.rt-actions__hint { max-width: 420px; }
.rt-disclaimer { margin-top: var(--st-space-10); font-size: var(--st-font-size-caption); }

@media (max-width: 720px) {
  .rt-form__grid, .rt-cards { grid-template-columns: 1fr; }
  .rt-hero { padding: var(--st-space-12) 0 var(--st-space-8); }
}
`
}

function fieldDocsStyles(): string {
  const dark = accents.amber.dark
  return `
/* 변수는 크롬(헤더·푸터)까지 덮도록 .site--fd 에 얹는다 — 안 그러면 라이트 모드에서
   흰 헤더와 검은 히어로 사이에 이음매가 그대로 보인다 */
.site--fd {
  ${themeVariables(themes.dark)}
  --st-color-accent: ${dark.accent};
  --st-color-accent-hover: ${dark.accentHover};
  --st-color-accent-surface: ${dark.accentSurface};
  --st-color-text-on-accent: ${dark.textOnAccent};
  background: var(--st-color-background);
  color: var(--st-color-text);
}
.fd { background: var(--st-color-background); color: var(--st-color-text); }

.fd-hero { padding: var(--st-space-20) 0 var(--st-space-16); }
.fd-hero__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.9fr);
  gap: var(--st-space-12);
  align-items: center;
}
.fd-kicker { display: block; margin-bottom: var(--st-space-4); }
.fd-h1 {
  margin: 0;
  font-size: clamp(2rem, 5.2vw, 3.25rem);
  font-weight: var(--st-font-weight-bold);
  line-height: 1.18;
  letter-spacing: -0.03em;
  color: var(--st-color-text);
}
.fd-h1__accent { color: var(--st-color-accent); }
.fd-sub { display: block; margin-top: var(--st-space-5); max-width: 30em; }

.fd-cta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-3);
  margin-top: var(--st-space-8);
}
/* 앵커를 버튼처럼 쓰므로 st-btn 의 인라인 정렬만 보정한다 */
.fd-cta__main { display: inline-flex; align-items: center; }
.fd-trust { display: block; margin-top: var(--st-space-4); }

/* ── 샘플 완료보고서 — 어두운 화면 위의 흰 서류 한 장 ── */
.fd-doc {
  background: var(--st-color-surface);
  border: 1px solid var(--st-color-border);
  border-radius: var(--st-radius-lg);
  padding: var(--st-space-6);
  /* 종이가 떠 있는 느낌 — 새 색을 만들지 않으려고 배경색을 섞어 그림자를 낸다 */
  box-shadow: 0 24px 48px color-mix(in srgb, var(--st-color-background) 70%, transparent);
}
.fd-doc__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--st-space-3);
  padding-bottom: var(--st-space-4);
  border-bottom: 1px solid var(--st-color-border);
}
.fd-doc__stamp {
  padding: 2px var(--st-space-2);
  border-radius: var(--st-radius-sm);
  background: var(--st-color-accent-surface);
  color: var(--st-color-accent);
  font-size: var(--st-font-size-xs);
  font-weight: var(--st-font-weight-semibold);
}
.fd-doc__meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--st-space-3);
  margin: var(--st-space-4) 0;
}
.fd-doc__meta dt {
  color: var(--st-color-text-subtle);
  font-size: var(--st-font-size-xs);
  margin-bottom: 2px;
}
.fd-doc__meta dd { margin: 0; color: var(--st-color-text); font-size: var(--st-font-size-sm); }

.fd-doc__table { width: 100%; border-collapse: collapse; font-size: var(--st-font-size-sm); }
.fd-doc__table th,
.fd-doc__table td { padding: var(--st-space-2); text-align: right; white-space: nowrap; }
.fd-doc__table th:first-child,
.fd-doc__table td:first-child { text-align: left; white-space: normal; word-break: keep-all; }
.fd-doc__table thead th {
  color: var(--st-color-text-subtle);
  font-size: var(--st-font-size-xs);
  font-weight: var(--st-font-weight-medium);
  border-bottom: 1px solid var(--st-color-border);
}
.fd-doc__table tbody td { color: var(--st-color-text-muted); }
.fd-doc__table tfoot td {
  color: var(--st-color-text-muted);
  border-top: 1px solid var(--st-color-border);
}
.fd-doc__table tfoot tr:not(:last-child) td { border-top: none; }
.fd-doc__total td {
  color: var(--st-color-text);
  font-weight: var(--st-font-weight-bold);
  border-top: 1px solid var(--st-color-border-strong);
}
.fd-doc__total td:last-child { color: var(--st-color-accent); }
.fd-doc__memo {
  margin: var(--st-space-4) 0 0;
  padding-top: var(--st-space-4);
  border-top: 1px dashed var(--st-color-border);
  color: var(--st-color-text-muted);
  font-size: var(--st-font-size-sm);
}

/* ── 밴드 ── */
.fd-band { padding: var(--st-space-20) 0; border-top: 1px solid var(--st-color-border); }
.fd-band--pricing { background: var(--st-color-surface); }
.fd-band__head { margin-bottom: var(--st-space-10); }
.fd-band__head .st-text--body { margin-top: var(--st-space-2); max-width: 34em; }

.fd-reasons {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--st-space-8);
}
.fd-reason { display: flex; flex-direction: column; gap: var(--st-space-2); }
.fd-reason__no { display: block; }

/* ── 요금 ── */
.fd-plans {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--st-space-5);
  max-width: 40rem;
}
.fd-plan {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
  padding: var(--st-space-6);
  background: var(--st-color-background);
  border: 1px solid var(--st-color-border);
  border-radius: var(--st-radius-lg);
}
.fd-plan__price {
  margin: 0;
  font-size: var(--st-font-size-2xl);
  font-weight: var(--st-font-weight-bold);
  letter-spacing: -0.02em;
  color: var(--st-color-text);
}
.fd-plan .st-btn { margin-top: var(--st-space-4); }
.fd-plan__ack { display: block; margin-top: var(--st-space-5); }

/* ── 체험 신청 ── */
.fd-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--st-space-5);
  max-width: 40rem;
}
.fd-form .st-btn { grid-column: 1 / -1; }
.fd-field { display: flex; flex-direction: column; gap: var(--st-space-2); }
.fd-field .st-select { width: 100%; }
.fd-privacy { display: block; margin-top: var(--st-space-8); max-width: 40em; }
.fd-done { display: flex; flex-direction: column; gap: var(--st-space-3); max-width: 34em; }

@media (max-width: 860px) {
  .fd-hero__grid { grid-template-columns: minmax(0, 1fr); gap: var(--st-space-10); }
  .fd-reasons { grid-template-columns: minmax(0, 1fr); gap: var(--st-space-6); }
  .fd-plans,
  .fd-form { grid-template-columns: minmax(0, 1fr); }
  .fd-hero { padding: var(--st-space-12) 0; }
  .fd-band { padding: var(--st-space-12) 0; }
}

/* ── 견적서 만들기 (유입 엔진, docs/goals §4-b) ── */
.qb-hero { padding: var(--st-space-16) 0 var(--st-space-10); }
.qb-hero__title { margin-top: var(--st-space-3); }
.qb-hero__lead { margin-top: var(--st-space-4); max-width: 660px; }
.qb-body { padding-bottom: var(--st-space-16); display: grid; gap: var(--st-space-6); }

.qb-form { padding: var(--st-space-6); display: grid; gap: var(--st-space-5); }
.qb-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--st-space-4); }
.qb-grid label { display: flex; flex-direction: column; gap: var(--st-space-2); }
.qb-form input, .qb-form select, .qb-form textarea {
  font: inherit;
  padding: var(--st-space-3);
  border: 1px solid var(--st-color-border);
  border-radius: var(--st-radius-md);
  background: var(--st-color-background);
  color: var(--st-color-text);
}
.qb-items { display: grid; gap: var(--st-space-2); }
.qb-item { display: grid; grid-template-columns: 1fr 84px 132px 116px; gap: var(--st-space-2); align-items: center; }
.qb-item__amount { text-align: right; color: var(--st-color-text-muted); font-variant-numeric: tabular-nums; }
.qb-actions { display: flex; gap: var(--st-space-3); flex-wrap: wrap; }
.qb-note { display: flex; flex-direction: column; gap: var(--st-space-2); }

/* 미리보기 = 인쇄되는 문서 그대로 */
.qb-doc { padding: var(--st-space-8); display: grid; gap: var(--st-space-6); }
.qb-doc__head { display: flex; justify-content: space-between; align-items: baseline; gap: var(--st-space-4); flex-wrap: wrap; }
.qb-doc__meta { display: flex; gap: var(--st-space-4); color: var(--st-color-text-muted); font-size: 0.9rem; }
.qb-doc__parties { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--st-space-4); }
.qb-doc__empty { padding: var(--st-space-6) 0; }
.qb-doc__note { display: grid; gap: var(--st-space-2); }

.qb-table { width: 100%; border-collapse: collapse; font-variant-numeric: tabular-nums; }
.qb-table th, .qb-table td { padding: var(--st-space-3); border-bottom: 1px solid var(--st-color-border); text-align: left; }
.qb-table th { color: var(--st-color-text-muted); font-weight: 600; font-size: 0.9rem; }
.qb-right { text-align: right; }
.qb-total td { font-weight: 700; border-bottom: none; }

.qb-upsell { padding: var(--st-space-6); display: grid; gap: var(--st-space-3); }
.qb-upsell__link { color: var(--st-color-accent); text-decoration: none; font-weight: 600; }

@media (max-width: 720px) {
  .qb-grid, .qb-doc__parties { grid-template-columns: minmax(0, 1fr); }
  .qb-item { grid-template-columns: 1fr 64px 100px; }
  .qb-item__amount { grid-column: 2 / -1; }
}

/* 인쇄 — 입력·안내를 빼고 문서만 남긴다. PDF 라이브러리 대신 브라우저 인쇄를 쓴다 */
@media print {
  .qb-hero, .qb-noprint, header, footer { display: none !important; }
  .qb-body { padding: 0; }
  .qb-doc { border: none; box-shadow: none; padding: 0; }
}

/* 작업완료보고서 — 견적서 껍데기를 쓰되 체크·서명란만 다르다 */
.cr-item { display: grid; grid-template-columns: 24px 1fr; gap: var(--st-space-3); align-items: center; }
.cr-item input[type='checkbox'] { width: 18px; height: 18px; accent-color: var(--st-color-accent); }
.cr-status { font-weight: 700; text-align: right; }
.cr-sign { display: grid; gap: var(--st-space-3); margin-top: var(--st-space-6); }
.cr-sign__line { border-bottom: 1px solid var(--st-color-border); height: var(--st-space-10); }

/* 무료 도구 목록 (홈) — 검색 유입구로 가는 내부 링크다 */
.tool-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: var(--st-space-4);
}
.tool-card {
  display: grid;
  gap: var(--st-space-2);
  padding: var(--st-space-5);
  border: 1px solid var(--st-color-border);
  border-radius: var(--st-radius-lg);
  background: var(--st-color-surface);
  text-decoration: none;
  color: inherit;
  transition: border-color 120ms ease, transform 120ms ease;
}
.tool-card:hover { border-color: var(--st-color-accent); transform: translateY(-2px); }
.tool-card__name { font-weight: 700; }
.tool-card__desc { font-size: 0.92rem; }
`
}

/**
 * 공용 지원 문의 화면(`/support`) — 앱이 열어 주는 입구다 (docs/support.md).
 *
 * **사이트 크롬 없이 단독으로 뜬다.** 앱 안에서 커스텀 탭·사파리 뷰로 열리는 화면이라
 * 회사 헤더와 푸터 내비게이션이 붙으면 사용자가 앱 밖으로 새어 나간다.
 *
 * 색은 하나도 안 정한다 — 앱이 넘긴 액센트가 `--st-*` 변수로 이미 갈려 있고, 여기서
 * 값을 박으면 그 통일성이 깨진다.
 */
function supportFormStyles(): string {
  // **테두리 두께 변수는 `--st-border-hairline` 이다** (2026-08-26 실측). 여기 오래 적혀
  // 있던 `--st-border-thin` 은 토큰에 없는 이름이라, `border: var(...) solid ...` 선언이
  // 통째로 무효가 되어 **문의 화면의 칩·입력칸에 테두리가 없었다.** 없는 변수는 조용히
  // 무시되므로 화면을 실제로 열어 보기 전에는 안 드러난다
  return `
.support-form {
  /* 한 손으로 쓰는 화면이라 한 단이다. 폭은 읽기 편한 줄길이에서 멈춘다 */
  max-width: 560px;
  margin: 0 auto;
  padding: var(--st-space-6) var(--st-space-5) var(--st-space-10);
  display: flex;
  flex-direction: column;
  gap: var(--st-space-6);
  /* 앱에서 열린 창이라 배경이 비면 브라우저 기본 흰색이 뚫고 나온다 */
  background: var(--st-color-background);
  min-height: 100vh;
  box-sizing: border-box;
}
.support-form__head {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}

/* 앱 안 웹뷰 — 상단바가 바로 위에 있으므로 제목 자리를 비우고 위 여백을 줄인다.
   min-height 도 끈다: 웹뷰는 이미 화면 한 판이라 100vh 를 주면 빈 아래가 스크롤로 남는다 */
.support-form--embed {
  padding-top: var(--st-space-5);
  min-height: 0;
}
.support-form form {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-6);
}
.support-form__field {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
  /* fieldset 의 기본 테두리·여백을 지운다 — 종류 칸도 같은 리듬으로 서야 한다 */
  border: 0;
  margin: 0;
  padding: 0;
  min-inline-size: 0;
}
.support-form__field legend {
  padding: 0;
  margin-bottom: var(--st-space-2);
}
.support-form__chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-2);
}

/* 칩은 진짜 라디오다 — 키보드 이동·스크린리더를 다시 만들지 않기 위해서다 */
.support-chip {
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 0 var(--st-space-4);
  border: var(--st-border-hairline) solid var(--st-color-border);
  border-radius: 999px;
  background: var(--st-color-surface);
  color: var(--st-color-text-muted);
  font-size: var(--st-font-size-sm);
  font-weight: var(--st-font-weight-medium);
  cursor: pointer;
  user-select: none;
}
.support-chip input {
  /* 눈에서만 지운다 — display:none 이면 키보드 포커스까지 사라진다 */
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
}
.support-chip--on {
  background: var(--st-color-accent-surface);
  border-color: var(--st-color-accent);
  color: var(--st-color-accent);
}
.support-chip:focus-within {
  outline: 2px solid var(--st-color-accent);
  outline-offset: 2px;
}

.support-form__textarea,
.support-form__input {
  width: 100%;
  box-sizing: border-box;
  padding: var(--st-space-4);
  border: var(--st-border-hairline) solid var(--st-color-border);
  border-radius: var(--st-radius-lg);
  background: var(--st-color-surface);
  color: var(--st-color-text);
  font-family: var(--st-font-family);
  /* 16px 미만이면 iOS 사파리가 칸을 누를 때 화면을 확대한다 */
  font-size: var(--st-font-size-md);
  line-height: var(--st-line-height-md);
}
.support-form__textarea {
  resize: vertical;
  min-height: 140px;
}
.support-form__textarea:focus-visible,
.support-form__input:focus-visible {
  outline: 2px solid var(--st-color-accent);
  outline-offset: 1px;
  border-color: var(--st-color-accent);
}
.support-form__textarea::placeholder,
.support-form__input::placeholder {
  color: var(--st-color-text-subtle);
}

.support-form__photos {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--st-space-3);
}
.support-photo {
  position: relative;
  width: 72px;
  height: 72px;
}
.support-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--st-radius-md);
  border: var(--st-border-hairline) solid var(--st-color-border);
}
.support-photo__remove {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 0;
  background: var(--st-color-text);
  color: var(--st-color-background);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
}

.support-form__note a {
  color: var(--st-color-accent);
}
.support-form__error {
  /* 실패 문구는 보내기 버튼 바로 위에 온다 — 누른 자리에서 읽혀야 한다 */
  margin: 0;
}
.support-form__done {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-3);
  margin-top: var(--st-space-8);
}
.support-form__receipt {
  font-family: var(--st-mono-font-family);
}
`
}

/**
 * 공용 "내 계정" 화면 (2026-08-26). 문의 화면과 같은 자리에 같은 리듬으로 선다 —
 * 앱 안 웹뷰에서 뜨므로 한 단·한 손 폭이고, 색·간격은 전부 토큰을 경유한다.
 *
 * **문의 폼의 클래스를 재사용하지 않는다.** 이름을 공유하면 한쪽 화면을 고칠 때 다른
 * 화면이 같이 움직이는데, 그 둘은 서로 다른 속도로 바뀐다 (docs/rules.md 디자인 절과
 * 같은 판단 — 값은 공유하되 구조는 각자).
 */
function accountStyles(): string {
  return `
.account {
  max-width: 560px;
  margin: 0 auto;
  padding: var(--st-space-6) var(--st-space-5) var(--st-space-10);
  display: flex;
  flex-direction: column;
  gap: var(--st-space-5);
  /* 앱에서 열린 화면이라 배경이 비면 브라우저 기본 흰색이 뚫고 나온다 */
  background: var(--st-color-background);
  min-height: 100vh;
  box-sizing: border-box;
}
/* 앱 안 웹뷰 — 상단바가 바로 위에 있으므로 제목 자리를 비우고 위 여백을 줄인다.
   min-height 도 끈다: 웹뷰는 이미 화면 한 판이라 100vh 를 주면 빈 아래가 스크롤로 남는다 */
.account--embed {
  padding-top: var(--st-space-4);
  min-height: 0;
}
.account__head { display: flex; flex-direction: column; gap: var(--st-space-2); }
.account__lead { margin-top: var(--st-space-2); }
.account__actions {
  display: flex;
  gap: var(--st-space-3);
  margin-top: var(--st-space-4);
}
.account__actions--stack { flex-direction: column; margin-top: var(--st-space-2); }

/* ── 신원 카드 ─────────────────────────────────────── */
.account-identity {
  display: flex;
  align-items: center;
  gap: var(--st-space-4);
}
.account-identity__text { flex: 1; min-width: 0; }
.account-avatar {
  width: 64px;
  height: 64px;
  border-radius: 999px;
  object-fit: cover;
  background: var(--st-color-surface-raised);
  flex: none;
}
.account-avatar--initial {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--st-font-size-xl);
  font-weight: var(--st-font-weight-bold);
  color: var(--st-color-text-muted);
  border: var(--st-border-hairline) solid var(--st-color-border);
}

/* ── 메뉴 행 ───────────────────────────────────────── */
.account-rows {
  display: flex;
  flex-direction: column;
  border: var(--st-border-hairline) solid var(--st-color-border);
  border-radius: var(--st-radius-lg);
  overflow: hidden;
  background: var(--st-color-surface);
}
.account-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--st-space-4);
  /* 한 손으로 누르는 화면이다 — 행 높이를 손가락에 맞춘다 */
  min-height: 64px;
  padding: var(--st-space-3) var(--st-space-4);
  border: 0;
  border-bottom: var(--st-border-hairline) solid var(--st-color-border);
  background: transparent;
  color: inherit;
  font-family: var(--st-font-family);
  text-align: left;
  cursor: pointer;
}
/* 마지막 행 밑에 선을 두지 않는다 — 아래 버튼 위에 선이 하나 더 떠 보인다
   (앱 시절 2026-08-10 사용자 제보로 고친 것과 같은 자리) */
.account-row:last-child { border-bottom: 0; }
.account-row__text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.account-row__chevron { color: var(--st-color-text-subtle); font-size: var(--st-font-size-lg); }
.account-row:focus-visible { outline: 2px solid var(--st-color-accent); outline-offset: -2px; }

/* ── 하위 화면 머리 ─────────────────────────────────── */
.account-subhead {
  display: flex;
  align-items: center;
  gap: var(--st-space-2);
}

/* ── 폼 ───────────────────────────────────────────── */
.account-form {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-5);
}
.account-field { display: flex; flex-direction: column; gap: var(--st-space-2); }
.account-field__input {
  width: 100%;
  box-sizing: border-box;
  padding: var(--st-space-4);
  border: var(--st-border-hairline) solid var(--st-color-border);
  border-radius: var(--st-radius-lg);
  background: var(--st-color-surface);
  color: var(--st-color-text);
  font-family: var(--st-font-family);
  /* 16px 미만이면 iOS 사파리가 칸을 누를 때 화면을 확대한다 */
  font-size: var(--st-font-size-md);
  line-height: var(--st-line-height-md);
}
.account-field__input:focus-visible {
  outline: 2px solid var(--st-color-accent);
  outline-offset: 1px;
  border-color: var(--st-color-accent);
}

/* ── 사진 ─────────────────────────────────────────── */
.account-photo { display: flex; align-items: center; gap: var(--st-space-4); }
.account-photo__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-2);
}

/* ── 연결된 계정 ───────────────────────────────────── */
.account-linked__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--st-space-4);
  padding: var(--st-space-3) 0;
  border-bottom: var(--st-border-hairline) solid var(--st-color-border);
}
.account-linked__row:last-child { border-bottom: 0; }

/* 되돌릴 수 없는 것(탈퇴·사진 삭제)만 이 색이다 — 위험을 색으로 먼저 말한다 */
.account-danger { color: var(--st-color-danger); }
`
}
